import {useEffect, useRef, useState} from "react";
import {toast} from "react-toastify";
import {RotatingLines} from "react-loader-spinner";
import {generateXReqId} from "../../utils";
import {ConfirmationDialog} from "../../components/confirmationDialog";
import {formStyle, trimValue, validLengthAndOnlyDigits} from "./index";

export const SearchForm = () => {
	const [isDisabledBasicFields, setIsDisabledBasicFields] = useState(false);
	const [isDisabledIdFields, setIsDisabledIdFields] = useState(false);
	const [loading, setLoading] = useState(false);

	const [isDeleteDialogVisible, setIsDeleteDialogVisible] = useState(false);

	const [deleteEntryMap, setDeleteEntryMap] = useState(null);

	const [afmValue, setAfmValue] = useState("");
	const [amkaValue, setAmkaValue] = useState("");
	const [documentIdValue, setDocumentIdValue] = useState('');

	const [afmError, setAfmError] = useState("");
	const [amkaError, setAmkaError] = useState("");

	const afmInputRef = useRef(null);
	const amkaInputRef = useRef(null);

	const [searchData, setSearchData] = useState([]);
	const [noResults, setNoResults] = useState(false);

	const [searchInputValues, setSearchInputValues] = useState({
		afm: "",
		amka: "",
		documentId: "",
		firstName: "",
		lastName: "",
		fatherName: "",
		birthdate: ""
	});

	const convertDateFormat = (dateString) => {
		const date = new Date(dateString);
		return date.toLocaleDateString("el-GR");
	};

	const isOkSearchValidations = (afm, amka, firstName, lastName, fatherName, birthdate) => {
		if (!isDisabledBasicFields && !isDisabledIdFields) {
			toast.error("Συμπληρώστε ΑΦΜ ή ΑΜΚΑ ή Αριθμό εγγράφου (ή συνδυασμό) \n (ή) Στοιχεία ταυτότητας");
			return false;
		} else if (isDisabledIdFields) {
			if (afm.length > 0 && !validLengthAndOnlyDigits(afm, 9)) {
				setAfmError("Το ΑΦΜ πρέπει να αποτελείται από 9 ψηφία");
				afmInputRef.current.focus();
				return false;
			}
			if (amka.length > 0 && !validLengthAndOnlyDigits(amka, 11)) {
				setAmkaError("Το AMKA πρέπει να αποτελείται από 11 ψηφία");
				amkaInputRef.current.focus();
				return false;
			}
		} else if (isDisabledBasicFields) {
			if (firstName.length === 0 || lastName.length === 0 || fatherName.length === 0 || birthdate.length === 0) {
				toast.error("Συμπληρώστε όλα τα στοιχεία ταυτότητας");
				return false;
			}
		}

		return true;
	};

	const handleSearchSubmit = async (event) => {
		event.preventDefault();
		const formData = new FormData(event.target);
		const afm = trimValue(formData.get("afm"));
		const amka = trimValue(formData.get("amka"));
		const documentId = trimValue(formData.get("documentId"));
		const firstName = trimValue(formData.get("firstName"));
		const lastName = trimValue(formData.get("lastName"));
		const fatherName = trimValue(formData.get("fatherName"));
		const birthdate = trimValue(formData.get("birthdate"));

		if (!isOkSearchValidations(afm, amka, firstName, lastName, fatherName, birthdate)) {
			return;
		}

		setLoading(true);
		const suspendedRequest = {
			afm: afm || null,
			amka: amka || null,
			documentId: documentId || null,
			firstName: firstName || null,
			fathersName: fatherName || null,
			lastName: lastName || null,
			dateOfBirth: birthdate || null
		};
		const res = await fetch("/api/blackList-administration/search", {
			method: "POST",
			credentials: "include",
			headers: {
				"Content-Type": "application/json",
				"x-request-id": generateXReqId()
			},
			body: JSON.stringify(suspendedRequest)
		});
		try {
			if (res.ok) {
				const data = await res.json();
				if (data.length > 0) {
					setSearchData(data);
				} else {
					setSearchData([]);
					setNoResults(true);
				}
			} else {
				throw res;
			}
		} catch (error) {
			const errorResponse = await error.text();
			try {
				const json = JSON.parse(errorResponse);
				const appError = json?.error;
				toast.error(appError);
			} catch (err) {
				toast.error(errorResponse);
			}
			setIsDisabledBasicFields(false);
			handleSearchFormReset();
		}
		setLoading(false);
	};

	const handleDelete = async (id, afm, amka, documentId) => {
		setDeleteEntryMap(new Map([
			["id", id],
			["afm", afm],
			["amka", amka],
			["documentId", documentId]
		]));
		setIsDeleteDialogVisible(true);
	};

	const handleDeleteConfirm = async () => {
		setIsDeleteDialogVisible(false);
		setLoading(true);
		const deleteRequest = {
			afm: deleteEntryMap.get("afm") || null,
			amka: deleteEntryMap.get("amka") || null,
			documentId: deleteEntryMap.get("documentId") || null
		};
		const res = await fetch("/api/blackList-administration/delete/".concat(deleteEntryMap.get("id")), {
			method: "PUT",
			credentials: "include",
			headers: {
				"Content-Type": "application/json",
				"x-request-id": generateXReqId()
			},
			body: JSON.stringify(deleteRequest)
		});
		try {
			if (res.ok) {
				setSearchData((prevData) => prevData.filter((entry) => entry.id !== deleteEntryMap.get("id")));
				toast.success("Η εγγραφή για το χρήστη διαγράφηκε επιτυχώς");
			} else {
				throw res;
			}
		} catch (error) {
			const errorResponse = await error.text();
			try {
				const json = JSON.parse(errorResponse);
				const appError = json?.error;
				toast.error(appError);
			} catch (err) {
				toast.error(errorResponse);
			}
		}
		setDeleteEntryMap(null);
		setLoading(false);
	};

	const handleDeleteCancel = () => {
		setDeleteEntryMap(null);
		setIsDeleteDialogVisible(false);
	};

	useEffect(() => {
		const { afm, amka, documentId, firstName, lastName, fatherName, birthdate } = searchInputValues;

		/*
		 * handle ID (basic) fields
		 */
		(afm.trim() || amka.trim() || documentId.trim()) ? setIsDisabledIdFields(true) : setIsDisabledIdFields(false);
		if (afm.length === 0 || (afm.length > 0 && validLengthAndOnlyDigits(afm, 9))) {
			setAfmError("");
		}
		if (amka.length === 0 || (amka.length > 0 && validLengthAndOnlyDigits(amka, 11))) {
			setAmkaError("");
		}
		setAfmValue(afm);
		setAmkaValue(amka);
		setDocumentIdValue(documentId);

		/*
		 * disable ID (basic) fields, if name search fields are entered
		 */
		if (firstName.trim() || lastName.trim() || fatherName.trim() || birthdate.trim()) {
			setIsDisabledBasicFields(true);
		} else {
			setIsDisabledBasicFields(false);
		}
	}, [searchInputValues]);


	const updateSearchInputValues = (event) => {
		const { name, value } = event.target;
		const trimmedValue = (name === "afm" || name === "amka" || name === "documentId") ? value.replace(/\s/g, '') : value;
		setSearchInputValues((prevState) => ({
			...prevState,
			[name]: trimmedValue
		}));
	};

	const handleSearchFormReset = () => {
		setSearchInputValues({
			afm: "",
			amka: "",
			documentId: "",
			firstName: "",
			lastName: "",
			fatherName: "",
			birthdate: ""
		});
		setSearchData([]);
		setNoResults(false);
	};

	return (
			<div>
				{ loading ? (
						<div className="d-flex flex-fill justify-content-center align-items-center">
							<RotatingLines
									visible={ true }
									height="96"
									width="96"
									color="grey"
									strokeWidth="5"
									strokeColor="#003476"
									animationDuration="0.75"
									ariaLabel="rotating-lines-loading"
							/>
						</div>
				) : (
						<div style={ { overflowX: 'auto' } }>
							{ searchData.length > 0 ? (
									<table id="results"
										   style={ {
											   width: '100%',
											   marginTop: '50px',
											   marginBottom: '50px',
											   borderCollapse: 'collapse',
											   textAlign: 'center',
											   tableLayout: 'auto'
										   } }>
										<thead>
										<tr style={ { backgroundColor: '#f2f2f2' } }>
											<th style={ { border: '1px solid #ddd', padding: '8px' } }>ΑΑ</th>
											<th style={ { border: '1px solid #ddd', padding: '8px' } }>ΑΦΜ</th>
											<th style={ { border: '1px solid #ddd', padding: '8px' } }>ΑΜΚΑ</th>
											<th style={ { border: '1px solid #ddd', padding: '8px' } }>Αριθμός εγγράφου</th>
											<th style={ { border: '1px solid #ddd', padding: '8px' } }>Ημ/νία δημιουργίας</th>
											<th style={ { border: '1px solid #ddd', padding: '8px' } }>Ημ/νία λήξης</th>
											<th style={ { border: '1px solid #ddd', padding: '8px' } }>Δημιουργός</th>
											<th style={ { padding: "10px", border: "1px solid #ddd" } }>Διαγραφή</th>
										</tr>
										</thead>
										<tbody>
										{ searchData.map((entry, index) => (
												<tr key={ index }>
													<td style={ { border: '1px solid #ddd', padding: '8px' } }>{ index + 1 }</td>
													<td style={ { border: '1px solid #ddd', padding: '8px' } }>{ entry.afm ?? "-" }</td>
													<td style={ { border: '1px solid #ddd', padding: '8px' } }>{ entry.amka ?? "-" }</td>
													<td style={ { border: '1px solid #ddd', padding: '8px' } }>{ entry.documentId
															?? "-" }</td>
													<td style={ { border: '1px solid #ddd', padding: '8px' } }>
														{ entry.createdOn ? convertDateFormat(entry.createdOn) : "-" }</td>
													<td style={ { border: '1px solid #ddd', padding: '8px' } }>
														{ convertDateFormat(entry.endDate ?? "-") }</td>
													<td style={ { border: '1px solid #ddd', padding: '8px' } }>
														{ entry.createdBy ?? "-" }</td>
													<td style={ { padding: "10px", border: "1px solid #ddd", textAlign: "center" } }>
														{ entry.active ? (
																<button
																		onClick={ () => handleDelete(entry.id, entry.afm, entry.amka,
																				entry.documentId) }
																		style={ {
																			background: "none",
																			border: "none",
																			cursor: "pointer",
																			color: "red"
																		} }
																		title="Delete">
																	<svg
																			xmlns="http://www.w3.org/2000/svg"
																			fill="none"
																			viewBox="0 0 24 24"
																			strokeWidth="1.5"
																			stroke="currentColor"
																			style={ { width: "20px", height: "20px" } }>
																		<path
																				strokeLinecap="round"
																				strokeLinejoin="round"
																				d="M6 18L18 6M6 6l12 12"/>
																	</svg>
																</button>) : (<span>-</span>) }
													</td>
												</tr>
										)) }
										</tbody>
									</table>
							) : noResults ? (
									<div id="results" style={ { ...outer } }>
										<div style={ { ...inner } }
											 className="p-4 border bg-body-tertiary border-primary rounded mb-4 col-7">
											<p className="mb-0">Δεν βρέθηκαν αποτελέσματα.</p>
										</div>
									</div>) : null }
							<form id="searchForm" style={ { ...formStyle } } onSubmit={ handleSearchSubmit }>
								<h3 style={ { textAlign: "center", marginBottom: "20px", color: "#007BFF" } }>Αναζήτηση εγγραφής με
									βάση:</h3>
								{/*1st Section*/ }
								<div style={ {
									marginBottom: "20px",
									padding: "15px",
									border: "1px solid #ddd",
									borderRadius: "8px",
									backgroundColor: "#f9f9f9"
								} }>
									<h4 style={ { textAlign: "center", marginBottom: "10px", color: "#555" } }>ΑΦΜ ή ΑΜΚΑ ή Αριθμό
										εγγράφου (ή συνδυασμό)</h4>
									{/* ΑΦΜ Field */ }
									<div style={ { marginBottom: "15px" } }>
										<label htmlFor="afm" className="form-label">ΑΦΜ</label>
										<input ref={ afmInputRef } name="afm" id="afm" type="text"
											   className="form-control form-control-lg"
											   value={ afmValue }
											   disabled={ isDisabledBasicFields }
											   onChange={ updateSearchInputValues }/>
										{ afmError && <span style={ { color: "red", fontSize: "13px" } }>{ afmError }</span> }
									</div>

									{/* ΑΜΚΑ Field */ }
									<div style={ { marginBottom: "15px" } }>
										<label htmlFor="amka" className="form-label">ΑΜΚΑ</label>
										<input ref={ amkaInputRef } name="amka" id="amka" type="text"
											   className="form-control form-control-lg"
											   value={ amkaValue }
											   disabled={ isDisabledBasicFields }
											   onChange={ updateSearchInputValues }/>
										{ amkaError && <span style={ { color: "red", fontSize: "13px" } }>{ amkaError }</span> }
									</div>

									{/* Αριθμός εγγράφου Field */ }
									<div style={ { marginBottom: "15px" } }>
										<label  htmlFor="documentId" className="form-label">Αριθμός εγγράφου</label>
										<input name="documentId" id="documentId" type="text"
											   className="form-control form-control-lg"
											   value={ documentIdValue }
											   disabled={ isDisabledBasicFields }
											   onChange={ updateSearchInputValues }/>
									</div>
								</div>
								<h4 style={ { textAlign: "center", marginBottom: "10px", color: "#555" } }>ή</h4>
								{/*2nd Section*/ }
								<div style={ {
									marginBottom: "20px",
									padding: "15px",
									border: "1px solid #ddd",
									borderRadius: "8px",
									backgroundColor: "#f9f9f9"
								} }>
									<h4 style={ { textAlign: "center", marginBottom: "10px", color: "#555" } }>Στοιχεία
										ταυτότητας για αναζήτηση μέσω ΑΦΜ</h4>
									<div style={ { marginBottom: "15px" } }>
										<label htmlFor="firstName" className="form-label">Όνομα</label>
										<input name="firstName" id="firstName" type="text"
											   className="form-control form-control-lg"
											   disabled={ isDisabledIdFields }
											   onChange={ updateSearchInputValues }/>
									</div>
									<div style={ { marginBottom: "15px" } }>
										<label htmlFor="lastName" className="form-label">Επώνυμο</label>
										<input name="lastName" id="lastName" type="text"
											   className="form-control form-control-lg"
											   disabled={ isDisabledIdFields }
											   onChange={ updateSearchInputValues }/>
									</div>
									<div style={ { marginBottom: "15px" } }>
										<label htmlFor="fatherName" className="form-label">Πατρώνυμο</label>
										<input name="fatherName" id="fatherName" type="text"
											   className="form-control form-control-lg"
											   disabled={ isDisabledIdFields }
											   onChange={ updateSearchInputValues }/>
									</div>
									<div style={ { marginBottom: "15px" } }>
										<label htmlFor="birthdate" className="form-label">Ημερομηνία γέννησης</label>
										<input name="birthdate" id="birthdate" type="date" max={ new Date().toISOString().split("T")[0] }
											   className="form-control form-control-lg"
											   disabled={ isDisabledIdFields }
											   onChange={ updateSearchInputValues }/>
									</div>
								</div>
								<div style={ { display: "flex", gap: "10px", marginTop: "15px", justifyContent: "center" } }>
									<button type="reset" onClick={ handleSearchFormReset }
											className="btn btn-outline-primary btn-lg">Καθαρισμός
									</button>
									<button type="submit" className="btn btn-primary btn-lg">Αναζήτηση</button>
								</div>
							</form>
							<ConfirmationDialog
									isVisible={ isDeleteDialogVisible }
									onConfirm={ handleDeleteCancel }
									onCancel={ handleDeleteConfirm }
									message="Είστε σίγουροι ότι θέλετε να διαγράψετε την εγγραφή;"
									confirmText="Άκυρο"
									cancelText="Διαγραφή"/>
						</div>)
				}
			</div>
	);
};

const outer = {
	display: "flex",
	justifyContent: "center",
	alignItems: "center",
	marginTop: "50px",
	marginBottom: "30px"
}

const inner = {
	backgroundColor: "#4caf50",
	color: "white",
	display: "flex",
	justifyContent: "center",
	alignItems: "center"
}
